<script lang="ts" setup>
import { required as veeRequiredValidator } from '@vee-validate/rules'
import { defineRule, Field as VeeField, Form as VeeForm } from 'vee-validate'

import { vMaska } from 'maska'
import { useBasketStore } from '@/store/basket/basket'

import { SelectedDiscount } from '@/types/basket'

import BasketErrorMessage from '@/components/Basket/basket-utils/BasketErrorMessage.vue'

type Props = {
  modelValue: string
  selectedDiscount: string
  confirmedDiscount: SelectedDiscount | null
  isButtonLoading: boolean
  error: string
}

const props = defineProps<Props>()

const emit = defineEmits(['setDiscount', 'updateModelValue', 'cancelDiscount'])

const { isMobile } = useDevice()

const basketStore = useBasketStore()

const buttonText = computed(() => {
  if (props?.selectedDiscount === SelectedDiscount.BONUSES) {
    return props.confirmedDiscount ? 'Отменить' : 'Списать'
  }

  return props?.confirmedDiscount ? 'Отменить' : 'Применить'
})

const updateModelValue = (value: string) => emit('updateModelValue', value)
const setDiscount = (setErrors: Function) => emit('setDiscount', setErrors)
const cancelDiscount = () => emit('cancelDiscount')

const onClickHandler = (setErrors: Function) => {
  props.confirmedDiscount ? cancelDiscount() : setDiscount(setErrors)
}

const options = computed(() => {
  const mask =
    props.selectedDiscount === SelectedDiscount.PROMOCODE ? '' : '####'

  return { mask }
})

const requiredDiscount = (value: string) => {
  if (!veeRequiredValidator(value)) {
    return props.selectedDiscount === SelectedDiscount.PROMOCODE
      ? 'Введите промокод'
      : 'Введите количество бонусов для списания'
  }

  return true
}

const discountField = ref<any>(null)

// Сбрасываем поле каждый раз при переключении радио кнопки
watch(
  () => props.selectedDiscount,
  () => discountField.value.reset(),
)

defineRule('required-discount', requiredDiscount)

const bonusesValue = computed(() => {
  return basketStore.data.bonuses.toSpendAmount || props.modelValue
})

const placeholder = computed(() => {
  const postfix =
    props.selectedDiscount === SelectedDiscount.PROMOCODE
      ? 'промокод'
      : 'бонусы'

  return `Введите ${postfix}`
})
</script>

<template>
  <vee-form v-slot="{ handleSubmit }" class="basket-discount-input">
    <vee-field
      v-slot="{ field, errors, setErrors }"
      ref="discountField"
      name="discount"
      vid="discount"
      rules="required-discount"
      :model-value="bonusesValue"
      @update:model-value="updateModelValue"
    >
      <div class="basket-discount-input__field">
        <the-input
          v-maska:[options]
          v-bind="field"
          hide-details
          :readonly="Boolean(confirmedDiscount)"
          :clearable="false"
          :placeholder="placeholder"
        >
          <template v-if="confirmedDiscount" #append-inner>
            <the-icon
              name="close"
              clickable
              @click="onClickHandler(setErrors)"
            />
          </template>
        </the-input>

        <the-button
          class="basket-discount-input__button"
          size="sm"
          font="sm"
          type="button"
          :radius="isMobile ? 'lg' : 'sm'"
          block
          :loading="isButtonLoading"
          :disabled="errors.length > 0"
          @click="handleSubmit(onClickHandler(setErrors))"
        >
          {{ buttonText }}
        </the-button>
      </div>

      <transition name="height">
        <basket-error-message
          v-if="errors.length !== 0 || error"
          class="basket-discount-input__error"
          :error-message="errors[0] || error"
        />
      </transition>
    </vee-field>
  </vee-form>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/animations.scss' as *;

.basket-discount-input {
  &__field {
    display: flex;
    gap: 0 16px;

    @include media('>=tablet') {
      gap: 0 24px;
    }
  }

  &__button {
    max-width: 120px;

    @include media('>=tablet') {
      max-width: 140px;
    }
  }

  &__error {
    padding-top: 25px;
  }
}
</style>
